// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import { useRouter } from 'expo-router';

import { Text } from '@/components/Themed';
import { useI18n } from '@/i18n';
import appColors from '@/theme/colors';
import { FormBars, Sparkline, formatToPar } from './components/Charts';
import { EmptyHint, StatBarRow, StatCard, StatRow, StatTile, StatTileRow, statColors } from './components/StatPieces';
import { formatShortDate } from './formatting';
import type { PlayerStatistics } from './types';

/** Rounds needed before a trend line says anything; below this the cards stay hidden. */
const TREND_MINIMUM_ROUNDS = 3;

/** Half a round of hand-entered holes before a shot-detail percentage is worth showing. */
const SHOT_DETAIL_MINIMUM_HOLES = 9;

/**
 * The second view: the personal golf numbers. Handicap movement, form, where the strokes go —
 * the "am I getting better" half of the page.
 */
export function ScoringView({ statistics }: { statistics: PlayerStatistics }) {
  const { t } = useI18n();
  const router = useRouter();
  const { scoring, overview } = statistics;
  const { distribution, records } = scoring;

  const averageDelta = useMemo(() => {
    if (scoring.averageToPar18 === null || scoring.previousYearAverageToPar18 === null) {
      return null;
    }
    const diff = Math.round((scoring.averageToPar18 - scoring.previousYearAverageToPar18) * 10) / 10;
    if (diff === 0) {
      return t('statistics.sameAsLastSeason');
    }
    return t('statistics.vsLastSeason', { delta: diff > 0 ? `+${diff}` : String(diff) });
  }, [scoring.averageToPar18, scoring.previousYearAverageToPar18, t]);

  const handicapDelta = useMemo(() => {
    const trend = scoring.handicapTrend;
    if (trend.length < 2) return null;
    const diff = trend[trend.length - 1].playingHandicap - trend[0].playingHandicap;
    if (diff === 0) return null;
    return diff < 0
      ? t('statistics.handicapDown', { delta: Math.abs(diff) })
      : t('statistics.handicapUp', { delta: diff });
  }, [scoring.handicapTrend, t]);

  const formBars = useMemo(() => {
    if (scoring.form.length === 0) return [];
    const best = Math.min(...scoring.form.map((point) => point.toPar));
    return scoring.form.map((point) => ({
      key: point.roundId,
      value: point.toPar,
      label: formatShortDate(point.playDate),
      best: point.toPar === best,
    }));
  }, [scoring.form]);

  const formAverage = useMemo(() => {
    if (scoring.form.length === 0) return null;
    const total = scoring.form.reduce((sum, point) => sum + point.toPar, 0);
    return Math.round((total / scoring.form.length) * 10) / 10;
  }, [scoring.form]);

  const buckets = [
    { key: 'eagles', count: distribution.eagles, color: statColors.eagle, label: t('statistics.eagle') },
    { key: 'birdies', count: distribution.birdies, color: statColors.birdie, label: t('statistics.birdie') },
    { key: 'pars', count: distribution.pars, color: statColors.par, label: t('statistics.par') },
    { key: 'bogeys', count: distribution.bogeys, color: statColors.bogey, label: t('statistics.bogey') },
    { key: 'doublePlus', count: distribution.doublePlus, color: statColors.doublePlus, label: t('statistics.doublePlus') },
  ];
  const maxBucket = Math.max(1, ...buckets.map((bucket) => bucket.count));
  const bestPar = useMemo(() => {
    if (scoring.byPar.length === 0) return null;
    return scoring.byPar.reduce((best, entry) => (entry.averageToPar < best.averageToPar ? entry : best));
  }, [scoring.byPar]);

  const hasTrend = overview.roundsPlayed >= TREND_MINIMUM_ROUNDS;

  // Shot detail is opt-in and typed in by hand, so a metric only earns a place once it covers
  // enough holes to mean something. Nothing shows at all for players who never turned it on.
  const shotDetail = scoring.shotDetail;
  const shotDetailRows = [
    shotDetail.holesWithFairway >= SHOT_DETAIL_MINIMUM_HOLES && shotDetail.fairwayHitPercent !== null
      ? {
          key: 'fairways',
          label: t('statistics.fairwaysHit'),
          value: `${shotDetail.fairwayHitPercent}%`,
          hint: t('statistics.shotDetailOfHoles', {
            part: shotDetail.fairwaysHit,
            total: shotDetail.holesWithFairway,
          }),
        }
      : null,
    shotDetail.holesWithGreenInRegulation >= SHOT_DETAIL_MINIMUM_HOLES &&
    shotDetail.greenInRegulationPercent !== null
      ? {
          key: 'gir',
          label: t('statistics.greensInRegulation'),
          value: `${shotDetail.greenInRegulationPercent}%`,
          hint: t('statistics.shotDetailOfHoles', {
            part: shotDetail.greensInRegulation,
            total: shotDetail.holesWithGreenInRegulation,
          }),
        }
      : null,
    shotDetail.holesWithPutts >= SHOT_DETAIL_MINIMUM_HOLES && shotDetail.puttsPer18 !== null
      ? {
          key: 'putts',
          label: t('statistics.puttsPerRound'),
          value: String(shotDetail.puttsPer18),
          hint: t('statistics.shotDetailPuttsHint', {
            perHole: shotDetail.puttsPerHole ?? 0,
            holes: shotDetail.holesWithPutts,
          }),
        }
      : null,
    shotDetail.puttsPerGreenInRegulation !== null
      ? {
          key: 'puttsPerGir',
          label: t('statistics.puttsPerGir'),
          value: String(shotDetail.puttsPerGreenInRegulation),
          hint: undefined,
        }
      : null,
  ].filter((row): row is { key: string; label: string; value: string; hint?: string } => row !== null);

  return (
    <View style={styles.container}>
      <StatTileRow>
        <StatTile
          testID="stat-average-to-par"
          value={scoring.averageToPar18 !== null ? formatToPar(scoring.averageToPar18) : '—'}
          label={t('statistics.averageToPar')}
          delta={averageDelta}
          deltaTone={
            scoring.averageToPar18 !== null
            && scoring.previousYearAverageToPar18 !== null
            && scoring.averageToPar18 > scoring.previousYearAverageToPar18
              ? 'neutral'
              : 'good'
          }
        />
        <StatTile
          value={scoring.bestRound ? String(scoring.bestRound.grossStrokes) : '—'}
          suffix={scoring.bestRound ? `(${formatToPar(scoring.bestRound.toPar)})` : undefined}
          label={
            scoring.bestRound
              ? t('statistics.bestRoundAt', {
                  course: scoring.bestRound.courseName,
                  date: formatShortDate(scoring.bestRound.playDate),
                })
              : t('statistics.bestRound')
          }
        />
      </StatTileRow>

      {hasTrend && scoring.handicapTrend.length >= 2 ? (
        <StatCard title={t('statistics.playingHandicap')}>
          <View style={styles.handicapRow}>
            <View>
              <Text style={styles.handicapValue}>
                {scoring.handicapTrend[scoring.handicapTrend.length - 1].playingHandicap}
              </Text>
              {handicapDelta ? <Text style={styles.handicapDelta}>{handicapDelta}</Text> : null}
            </View>
            <Sparkline
              testID="handicap-sparkline"
              values={scoring.handicapTrend.map((point) => point.playingHandicap)}
            />
          </View>
          <Text style={styles.footnote}>{t('statistics.playingHandicapNote')}</Text>
        </StatCard>
      ) : null}

      {hasTrend && formBars.length >= 2 ? (
        <StatCard title={t('statistics.formTitle', { count: formBars.length })}>
          <FormBars
            bars={formBars}
            average={formAverage}
            averageLabel={t('statistics.average')}
            bestLabel={t('statistics.formLegend')}
            onSelect={(roundId) => router.push(`/round/${roundId}/scorecard`)}
          />
        </StatCard>
      ) : null}

      <StatCard title={t('statistics.scoreDistribution', { holes: distribution.totalScored })}>
        {distribution.totalScored === 0 ? (
          <EmptyHint text={t('statistics.noHoles')} />
        ) : (
          <>
            {buckets.map((bucket) => (
              <StatBarRow
                key={bucket.key}
                testID={`stat-bucket-${bucket.key}`}
                label={bucket.label}
                fraction={bucket.count / maxBucket}
                color={bucket.color}
                value={t('statistics.countWithShare', {
                  count: bucket.count,
                  share: ((bucket.count / distribution.totalScored) * 100).toFixed(1),
                })}
              />
            ))}
            {distribution.pickups > 0 ? (
              <Text style={styles.footnote}>{t('statistics.pickupsExcluded', { count: distribution.pickups })}</Text>
            ) : null}
          </>
        )}
      </StatCard>

      {scoring.byPar.length > 0 ? (
        <StatCard title={t('statistics.averageOverPar')}>
          <View style={styles.parRow}>
            {scoring.byPar.map((entry) => (
              <View
                key={entry.par}
                style={[styles.parCell, bestPar?.par === entry.par && styles.parCellBest]}
                testID={`stat-par-${entry.par}`}
              >
                <Text style={[styles.parValue, bestPar?.par === entry.par && styles.parValueBest]}>
                  {formatToPar(entry.averageToPar, 2)}
                </Text>
                <Text style={styles.parLabel}>{t('statistics.parN', { par: entry.par })}</Text>
              </View>
            ))}
          </View>
          {scoring.frontNineToPar !== null || scoring.backNineToPar !== null ? (
            <View style={styles.ninesRow}>
              <Text style={styles.nineText}>
                {t('statistics.frontNine')}{' '}
                <Text style={styles.nineValue}>
                  {scoring.frontNineToPar !== null ? formatToPar(scoring.frontNineToPar) : '—'}
                </Text>
              </Text>
              <Text style={styles.nineText}>
                {t('statistics.backNine')}{' '}
                <Text style={styles.nineValue}>
                  {scoring.backNineToPar !== null ? formatToPar(scoring.backNineToPar) : '—'}
                </Text>
              </Text>
            </View>
          ) : null}
        </StatCard>
      ) : null}

      {shotDetailRows.length > 0 ? (
        <StatCard title={t('statistics.shotDetail')}>
          {shotDetailRows.map((row) => (
            <StatRow
              key={row.key}
              label={row.label}
              value={row.value}
              hint={row.hint}
              testID={`stat-shot-detail-${row.key}`}
            />
          ))}
        </StatCard>
      ) : null}

      <StatCard title={t('statistics.recordsAndStreaks')}>
        <StatRow
          label={t('statistics.mostBirdies')}
          value={String(records.mostBirdiesInRound)}
          testID="stat-most-birdies"
        />
        <StatRow
          label={t('statistics.longestStreak')}
          value={t('statistics.holesCount', { count: records.longestParOrBetterStreak })}
        />
        {records.bestComeback !== null ? (
          <StatRow label={t('statistics.bestComeback')} value={formatToPar(records.bestComeback)} />
        ) : null}
        {records.holeInOnes > 0 ? (
          <StatRow label={t('statistics.holeInOnes')} value={String(records.holeInOnes)} />
        ) : null}
        {records.nemesisHole ? (
          <StatRow
            label={t('statistics.nemesisHole')}
            value={t('statistics.nemesisValue', {
              hole: records.nemesisHole.holeNumber,
              course: records.nemesisHole.courseName,
            })}
            hint={t('statistics.nemesisHint', {
              average: formatToPar(records.nemesisHole.averageToPar, 2),
              plays: records.nemesisHole.plays,
            })}
          />
        ) : null}
      </StatCard>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: 10,
  },
  handicapRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
  },
  handicapValue: {
    fontSize: 30,
    fontWeight: '800',
    color: appColors.text.heading,
  },
  handicapDelta: {
    fontSize: 12,
    fontWeight: '700',
    color: appColors.accent.primary,
  },
  footnote: {
    fontSize: 10.5,
    color: appColors.text.muted,
  },
  parRow: {
    flexDirection: 'row',
    gap: 8,
  },
  parCell: {
    flex: 1,
    alignItems: 'center',
    backgroundColor: appColors.background.panelAlt,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    borderRadius: 10,
    paddingVertical: 8,
  },
  parCellBest: {
    borderColor: appColors.accent.subtleBorder,
  },
  parValue: {
    fontSize: 17,
    fontWeight: '800',
    color: appColors.text.heading,
  },
  parValueBest: {
    color: appColors.accent.primary,
  },
  parLabel: {
    fontSize: 10.5,
    color: appColors.text.muted,
    marginTop: 1,
  },
  ninesRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 12,
  },
  nineText: {
    fontSize: 12,
    color: appColors.text.secondary,
  },
  nineValue: {
    fontSize: 14,
    fontWeight: '700',
    color: appColors.text.heading,
  },
});
